import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { PageHeader } from '@/components/common/page-header'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Input } from '@/components/ui/input'
import { Plus, Search, MoreVertical, Copy, Check, UserPlus, AlertCircle, CreditCard } from 'lucide-react'

const XCircleIcon = () => (
  <svg style={{ width: 18, height: 18 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M15 9l-6 6" />
    <path d="M9 9l6 6" />
  </svg>
)
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuthStore } from '@/store/auth-store'
import { usersService } from '@/services/users-service'
import { billingService, type SeatPreviewResult } from '@/services/billing-service'
import type { User } from '@/types'

const ROLE_OPTIONS = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'MANAGER', label: 'Manager' },
  { value: 'ESTIMATOR', label: 'Estimator' },
]

const roleLabel = (role: string) => {
  switch (role) {
    case 'SUPERADMIN': return 'Super Admin'
    case 'ADMIN': return 'Admin'
    case 'MANAGER': return 'Manager'
    case 'ESTIMATOR': return 'Estimator'
    default: return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()
  }
}

const roleColor = (role: string) => {
  switch (role) {
    case 'SUPERADMIN': return 'destructive'
    case 'ADMIN': return 'success'
    case 'MANAGER': return 'warning'
    case 'ESTIMATOR': return 'info'
    default: return 'secondary'
  }
}

/* Inline SVG menu icons */
const EditIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--s400)' }}>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
)
const ResendIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--s400)' }}>
    <path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4 20-7z" />
  </svg>
)
const ReactivateIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--s400)' }}>
    <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
)
const DeactivateIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
  </svg>
)

/* Separate component so Radix DropdownMenu always gets static children */
function UserRowMenu({ user, onEdit, onResendInvite, onReactivate, onDeactivate }: {
  user: User
  onEdit: () => void
  onResendInvite: () => void
  onReactivate: () => void
  onDeactivate: () => void
}) {
  const status = user.status?.toLowerCase()
  const isActive = status === 'active'
  const isInactive = status === 'inactive'

  const items: { label: string; icon: React.ReactNode; destructive?: boolean; action: () => void }[] = [
    { label: 'Edit', icon: <EditIcon />, action: onEdit },
  ]

  if (!isActive) {
    items.push({ label: 'Resend Invite', icon: <ResendIcon />, action: onResendInvite })
  }

  if (isInactive) {
    items.push({ label: 'Reactivate', icon: <ReactivateIcon />, action: onReactivate })
  } else {
    items.push({ label: 'Deactivate', icon: <DeactivateIcon />, destructive: true, action: onDeactivate })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {items.map((item) => (
          <DropdownMenuItem
            key={item.label}
            onClick={item.action}
            className={item.destructive ? 'text-destructive' : undefined}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            {item.icon}
            {item.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function UsersPage() {
  const currentUser = useAuthStore((s) => s.user)
  const [users, setUsers] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [error, setError] = useState('')

  // Invite modal
  interface InviteRow { id: string; firstName: string; lastName: string; email: string; role: string }
  interface InviteResult { email: string; displayName: string; invitationLink: string }
  const newRow = (): InviteRow => ({ id: Math.random().toString(36).slice(2), firstName: '', lastName: '', email: '', role: 'ESTIMATOR' })
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteRows, setInviteRows] = useState<InviteRow[]>([newRow()])
  const [inviteSubmitting, setInviteSubmitting] = useState(false)
  const [inviteErrors, setInviteErrors] = useState<Record<string, string>>({})
  const [inviteResults, setInviteResults] = useState<InviteResult[]>([])
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [inviteStep, setInviteStep] = useState<'form' | 'confirm'>('form')
  const [seatPreview, setSeatPreview] = useState<SeatPreviewResult | null>(null)
  const [seatPreviewLoading, setSeatPreviewLoading] = useState(false)

  // Edit modal
  const [editOpen, setEditOpen] = useState(false)
  const [editUser, setEditUser] = useState<User | null>(null)
  const [editDisplayName, setEditDisplayName] = useState('')
  const [editRole, setEditRole] = useState('')
  const [editSubmitting, setEditSubmitting] = useState(false)
  const [editError, setEditError] = useState('')

  // Deactivate modal
  const [deactivateOpen, setDeactivateOpen] = useState(false)
  const [deactivateUser, setDeactivateUser] = useState<User | null>(null)
  const [deactivateSubmitting, setDeactivateSubmitting] = useState(false)

  // Toast
  const [toast, setToast] = useState<string | null>(null)
  const toastTimer = useRef<ReturnType<typeof setTimeout>>()

  const showToast = (msg: string) => {
    setToast(msg)
    if (toastTimer.current) clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => setToast(null), 3000)
  }

  const loadUsers = async () => {
    try {
      setIsLoading(true)
      const data = await usersService.list()
      setUsers(data)
      setError('')
    } catch {
      setError('Failed to load users')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadUsers()
  }, [])

  const filtered = users.filter(
    (u) =>
      u.displayName.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase())
  )

  // --- Invite Users ---
  const openInvite = () => {
    setInviteRows([newRow()])
    setInviteErrors({})
    setInviteResults([])
    setCopiedId(null)
    setInviteStep('form')
    setSeatPreview(null)
    setInviteOpen(true)
  }

  const updateRow = (id: string, field: keyof InviteRow, value: string) => {
    setInviteRows((rows) => rows.map((r) => r.id === id ? { ...r, [field]: value } : r))
    setInviteErrors((errs) => { const next = { ...errs }; delete next[id]; return next })
  }

  const addRow = () => setInviteRows((rows) => [...rows, newRow()])

  const removeRow = (id: string) => setInviteRows((rows) => rows.filter((r) => r.id !== id))

  const handleInviteNext = async (e: React.FormEvent) => {
    e.preventDefault()
    const valid = inviteRows.filter((r) => r.email.trim() && r.firstName.trim() && r.lastName.trim())
    if (valid.length === 0) return
    setSeatPreviewLoading(true)
    try {
      const preview = await billingService.getSeatPreview(valid.length)
      setSeatPreview(preview)
      if (preview.stripeConfigured) {
        setInviteStep('confirm')
      } else {
        await handleInvite()
      }
    } catch {
      await handleInvite()
    } finally {
      setSeatPreviewLoading(false)
    }
  }

  const handleInvite = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    const valid = inviteRows.filter((r) => r.email.trim() && r.firstName.trim() && r.lastName.trim())
    if (valid.length === 0) return
    setInviteSubmitting(true)
    setInviteErrors({})
    const results: InviteResult[] = []
    const errors: Record<string, string> = {}
    await Promise.all(valid.map(async (row) => {
      try {
        const displayName = `${row.firstName.trim()} ${row.lastName.trim()}`
        const result = await usersService.create({ email: row.email.trim(), displayName, role: row.role })
        results.push({ email: row.email.trim(), displayName, invitationLink: result.invitationLink })
      } catch (err: any) {
        errors[row.id] = err?.response?.data?.message || 'Failed to invite'
      }
    }))
    setInviteSubmitting(false)
    if (results.length > 0) {
      setInviteResults(results)
      loadUsers()
    }
    if (Object.keys(errors).length > 0) {
      setInviteErrors(errors)
      // Per-row errors only render on the form step. Without this, a failure on
      // the billing-confirm step left the user staring at an unchanged dialog
      // with no feedback — exactly how the July 2026 invite outage presented.
      if (results.length === 0) setInviteStep('form')
    }
  }

  const handleCopyLink = (link: string, key: string) => {
    navigator.clipboard.writeText(link)
    setCopiedId(key)
    setTimeout(() => setCopiedId(null), 2000)
  }

  // --- Edit User ---
  const openEdit = (user: User) => {
    setEditUser(user)
    setEditDisplayName(user.displayName)
    setEditRole(user.role)
    setEditError('')
    setEditOpen(true)
  }

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editUser || !editDisplayName.trim()) return
    setEditSubmitting(true)
    setEditError('')
    try {
      await usersService.update(editUser.id, { displayName: editDisplayName.trim(), role: editRole })
      setEditOpen(false)
      showToast('User updated successfully')
      loadUsers()
    } catch (err: any) {
      setEditError(err?.response?.data?.message || 'Failed to update user')
    } finally {
      setEditSubmitting(false)
    }
  }

  // --- Deactivate / Reactivate ---
  const openDeactivate = (user: User) => {
    setDeactivateUser(user)
    setDeactivateOpen(true)
  }

  const handleDeactivate = async () => {
    if (!deactivateUser) return
    setDeactivateSubmitting(true)
    try {
      await usersService.update(deactivateUser.id, { status: 'inactive' })
      setDeactivateOpen(false)
      showToast(`${deactivateUser.displayName} has been deactivated`)
      loadUsers()
    } catch {
      showToast('Failed to deactivate user')
    } finally {
      setDeactivateSubmitting(false)
    }
  }

  const handleReactivate = async (user: User) => {
    try {
      await usersService.update(user.id, { status: 'active' })
      showToast(`${user.displayName} has been reactivated`)
      loadUsers()
    } catch {
      showToast('Failed to reactivate user')
    }
  }

  // --- Resend Invite ---
  const handleResendInvite = async (user: User) => {
    try {
      await usersService.resendInvite(user.id)
      showToast(`Invitation resent to ${user.email}`)
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'Failed to resend invitation')
    }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader title="User Management" description="Manage your team members and their roles">
        <Button onClick={openInvite}>
          <Plus className="h-4 w-4" />
          Invite User
        </Button>
      </PageHeader>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      {/* Loading */}
      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-16">
            <div className="usr-spinner" />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="divide-y">
              {filtered.length === 0 ? (
                <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                  {search ? 'No users match your search.' : 'No users found.'}
                </div>
              ) : (
                filtered.map((user) => {
                  const initials = user.displayName
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)

                  const isSelf = user.id === currentUser?.id

                  return (
                    <div key={user.id} className="flex items-center justify-between p-4 hover:bg-accent/50 transition-colors">
                      <div className="flex items-center gap-4">
                        <Avatar>
                          {user.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user.displayName} />}
                          <AvatarFallback className="bg-primary/10 text-primary text-sm">
                            {initials}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-sm">
                            {user.displayName}
                            {isSelf && <span className="text-muted-foreground ml-1">(You)</span>}
                          </p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {user.status?.toLowerCase() !== 'active' && (
                          <span className="hidden sm:inline text-xs italic" style={{ color: '#a1a1aa' }}>
                            pending invite
                          </span>
                        )}
                        {user.status?.toLowerCase() === 'inactive' ? (
                          <Badge variant="secondary" className="hidden sm:inline-flex" style={{ background: '#f0f0f3', color: '#a1a1aa' }}>
                            {roleLabel(user.role)}
                          </Badge>
                        ) : (
                          <Badge variant={roleColor(user.role) as 'default'} className="hidden sm:inline-flex">
                            {roleLabel(user.role)}
                          </Badge>
                        )}
                        {!isSelf && user.role !== 'SUPERADMIN' && (
                          <UserRowMenu
                            user={user}
                            onEdit={() => openEdit(user)}
                            onResendInvite={() => handleResendInvite(user)}
                            onReactivate={() => handleReactivate(user)}
                            onDeactivate={() => openDeactivate(user)}
                          />
                        )}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invite Users Modal */}
      {inviteOpen && createPortal(
        <div
          className="usr-modal-backdrop"
          onMouseDown={(e) => {
            // Only close when the press starts on the backdrop itself — prevents drag-to-select
            // inside an input from closing the modal when the release lands on the backdrop.
            if (e.target === e.currentTarget && !inviteSubmitting) setInviteOpen(false);
          }}
        >
          <div className="usr-modal usr-modal-lg" onMouseDown={(e) => e.stopPropagation()}>
            {inviteResults.length > 0 ? (
              /* ── Success state ── */
              <div className="usr-modal-body">
                <div className="usr-success-icon">
                  <Check className="h-6 w-6" />
                </div>
                <p className="usr-success-text">
                  {inviteResults.length === 1
                    ? `Invitation sent to ${inviteResults[0].displayName}!`
                    : `${inviteResults.length} invitations sent!`}
                </p>
                <p className="usr-success-subtext">
                  Share these links directly if needed:
                </p>
                <div className="usr-success-links">
                  {inviteResults.map((r) => (
                    <div key={r.email}>
                      <p className="usr-label" style={{ marginBottom: '4px' }}>{r.displayName} &mdash; {r.email}</p>
                      <div className="usr-link-row">
                        <input className="usr-link-input" value={r.invitationLink} readOnly />
                        <button className="usr-link-copy" onClick={() => handleCopyLink(r.invitationLink, r.email)}>
                          {copiedId === r.email ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {Object.keys(inviteErrors).length > 0 && (
                  <div className="usr-error">
                    <AlertCircle className="h-4 w-4 usr-error-icon" />
                    <div>
                      <p className="usr-error-title">
                        {Object.keys(inviteErrors).length === 1 ? 'This invitation failed:' : 'These invitations failed:'}
                      </p>
                      <ul className="usr-error-list">
                        {inviteRows.filter((r) => inviteErrors[r.id]).map((r) => (
                          <li key={r.id}>{r.firstName} {r.lastName} ({r.email}) &mdash; {inviteErrors[r.id]}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                <Button className="w-full" onClick={() => setInviteOpen(false)}>Done</Button>
              </div>
            ) : inviteStep === 'confirm' && seatPreview?.stripeConfigured ? (
              /* ── Billing confirmation step ── */
              <>
                <div className="usr-modal-header">
                  <div className="usr-modal-icon usr-modal-icon--billing">
                    <CreditCard className="h-[18px] w-[18px]" />
                  </div>
                  <div className="usr-modal-heading">
                    <p className="usr-modal-title-inline">Confirm Billing</p>
                    <p className="usr-modal-subtitle">
                      Adding {inviteRows.filter((r) => r.email.trim() && r.firstName.trim() && r.lastName.trim()).length === 1 ? 'this user' : 'these users'} will increase your subscription.
                    </p>
                  </div>
                </div>
                <div className="usr-modal-body">
                  <div className="usr-billing-summary">
                    <div className="usr-billing-row">
                      <span className="text-sm text-muted-foreground">Current seats</span>
                      <span className="text-sm font-medium">{seatPreview.currentSeats}</span>
                    </div>
                    <div className="usr-billing-row">
                      <span className="text-sm text-muted-foreground">New seats</span>
                      <span className="text-sm font-medium">{seatPreview.newSeats}</span>
                    </div>
                    <div className="usr-billing-row usr-billing-row-total">
                      <span className="text-sm font-medium">Price per seat / {seatPreview.billingInterval}</span>
                      <span className="usr-billing-price">
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: seatPreview.currency.toUpperCase() }).format((seatPreview.pricePerSeat ?? 0) / 100)}
                      </span>
                    </div>
                  </div>

                  <div className="usr-invite-people">
                    {inviteRows.filter((r) => r.email.trim() && r.firstName.trim() && r.lastName.trim()).map((row) => (
                      <div key={row.id} className="usr-invite-person">
                        <div className="usr-invite-avatar">
                          {`${row.firstName[0]}${row.lastName[0]}`.toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{row.firstName} {row.lastName}</p>
                          <p className="text-xs text-muted-foreground">{row.email}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="usr-modal-actions">
                    <Button type="button" variant="outline" onClick={() => setInviteStep('form')} disabled={inviteSubmitting}>
                      ← Back
                    </Button>
                    <Button onClick={() => handleInvite()} disabled={inviteSubmitting}>
                      {inviteSubmitting ? 'Sending...' : 'Confirm & Send Invite'}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              /* ── Form state ── */
              <>
                <div className="usr-modal-header">
                  <div className="usr-modal-icon">
                    <UserPlus className="h-[18px] w-[18px]" />
                  </div>
                  <div className="usr-modal-heading">
                    <p className="usr-modal-title-inline">Invite Team Members</p>
                    <p className="usr-modal-subtitle">Add people to your team and assign their role.</p>
                  </div>
                </div>
                <form onSubmit={handleInviteNext} className="usr-modal-body">

                  {Object.keys(inviteErrors).length > 0 && (
                    <div className="usr-error">
                      {Object.keys(inviteErrors).length === 1 ? 'This invitation could not be sent.' : 'These invitations could not be sent.'} See the details below each row.
                    </div>
                  )}

                  {/* Column headers */}
                  <div className="usr-invite-cols" style={{ marginBottom: '-4px' }}>
                    <span className="usr-invite-col-header">First Name</span>
                    <span className="usr-invite-col-header">Last Name</span>
                    <span className="usr-invite-col-header">Email</span>
                    <span className="usr-invite-col-header">Role</span>
                    <span />
                  </div>

                  {/* Invite rows */}
                  <div className="usr-invite-rows">
                    {inviteRows.map((row) => (
                      <div key={row.id}>
                        <div className="usr-invite-cols">
                          <Input
                            value={row.firstName}
                            onChange={(e) => updateRow(row.id, 'firstName', e.target.value)}
                            placeholder="John"
                          />
                          <Input
                            value={row.lastName}
                            onChange={(e) => updateRow(row.id, 'lastName', e.target.value)}
                            placeholder="Smith"
                          />
                          <Input
                            type="email"
                            value={row.email}
                            onChange={(e) => updateRow(row.id, 'email', e.target.value)}
                            placeholder="john@company.com"
                          />
                          <select
                            className="usr-select"
                            value={row.role}
                            onChange={(e) => updateRow(row.id, 'role', e.target.value)}
                          >
                            {ROLE_OPTIONS.map((r) => (
                              <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => removeRow(row.id)}
                            disabled={inviteRows.length === 1}
                            className="tbl-action-btn tbl-action-btn--danger"
                            style={{ opacity: inviteRows.length === 1 ? 0 : 1, pointerEvents: inviteRows.length === 1 ? 'none' : 'auto' }}
                          >
                            <XCircleIcon />
                          </button>
                        </div>
                        {inviteErrors[row.id] && (
                          <p className="usr-invite-row-error">{inviteErrors[row.id]}</p>
                        )}
                      </div>
                    ))}
                  </div>

                  <button type="button" onClick={addRow} className="usr-add-another">
                    <Plus className="h-4 w-4" />
                    Add another
                  </button>

                  <div className="usr-modal-actions">
                    <Button type="button" variant="outline" onClick={() => setInviteOpen(false)} disabled={inviteSubmitting || seatPreviewLoading}>
                      Cancel
                    </Button>
                    {(() => {
                      const readyCount = inviteRows.filter((r) => r.email.trim() && r.firstName.trim() && r.lastName.trim()).length
                      return (
                        <Button type="submit" disabled={inviteSubmitting || seatPreviewLoading || readyCount === 0}>
                          {seatPreviewLoading ? 'Checking...' : readyCount > 1 ? `Review ${readyCount} Invites →` : 'Review Invite →'}
                        </Button>
                      )
                    })()}
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      , document.body)}

      {/* Edit User Modal */}
      {editOpen && editUser && createPortal(
        <div className="usr-modal-backdrop" onClick={() => !editSubmitting && setEditOpen(false)}>
          <div className="usr-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="usr-modal-title">Edit User</h2>
            <form onSubmit={handleEdit} className="usr-modal-body">
              <div className="usr-field">
                <label className="usr-label">Email</label>
                <Input value={editUser.email} disabled className="bg-muted" />
              </div>
              <div className="usr-field">
                <label className="usr-label">Display Name</label>
                <Input
                  value={editDisplayName}
                  onChange={(e) => setEditDisplayName(e.target.value)}
                  required
                />
              </div>
              <div className="usr-field">
                <label className="usr-label">Role</label>
                <select
                  className="usr-select"
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value)}
                >
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              {editError && <div className="usr-error">{editError}</div>}
              <div className="usr-modal-actions">
                <Button type="button" variant="outline" onClick={() => setEditOpen(false)} disabled={editSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={editSubmitting || !editDisplayName.trim()}>
                  {editSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      , document.body)}

      {/* Deactivate Confirmation Modal */}
      {deactivateOpen && deactivateUser && createPortal(
        <div className="usr-modal-backdrop" onClick={() => !deactivateSubmitting && setDeactivateOpen(false)}>
          <div className="usr-modal usr-modal-sm" onClick={(e) => e.stopPropagation()}>
            <h2 className="usr-modal-title">Deactivate User</h2>
            <div className="usr-modal-body">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to deactivate <strong>{deactivateUser.displayName}</strong>? They will no longer be able to log in.
              </p>
              <p className="text-sm text-muted-foreground" style={{ marginTop: '8px' }}>
                Removing this user will reduce your subscription by 1 seat.
              </p>
              <div className="usr-modal-actions">
                <Button type="button" variant="outline" onClick={() => setDeactivateOpen(false)} disabled={deactivateSubmitting}>
                  Cancel
                </Button>
                <Button variant="destructive" onClick={handleDeactivate} disabled={deactivateSubmitting}>
                  {deactivateSubmitting ? 'Deactivating...' : 'Deactivate'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Toast */}
      {toast && (
        <div className="usr-toast">{toast}</div>
      )}
    </div>
  )
}
